/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useState } from 'react';

export type HeadingStructureItem = {
  elementText: string;
  subHeadings: string[];
};

/**
 * Get the structure of h2 and h3 headings within a container element, returning them in the shape of
 * ```ts
 * type HeadingStructureItem = {
 *   elementText: string; // The text content of the h2 element
 *   subHeadings: string[]; // The text content of the h3 elements nested under the h2 element
 * }
 * ```
 *
 * @param contentContainer The container element to extract headings from.
 * @returns An array of heading structure elements.
 */
export const useHeadingStructure = (contentContainer: HTMLElement | null) => {
  const [headingStructure, setHeadingStructure] = useState<HeadingStructureItem[]>([]);

  useEffect(() => {
    const structure: HeadingStructureItem[] = [];
    const allHeadings = Array.from(contentContainer?.querySelectorAll('h2, h3') || []);
    let currentHeading: HeadingStructureItem | null = null;
    allHeadings.forEach((heading) => {
      if (heading.tagName.toLowerCase() === 'h2') {
        currentHeading = {
          elementText: heading.textContent ?? '',
          subHeadings: [],
        };
        structure.push(currentHeading);
      } else {
        currentHeading?.subHeadings.push(heading.textContent ?? '');
      }
    });

    setHeadingStructure(structure);
  }, [contentContainer]);

  return headingStructure;
};
